import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Paper, Typography } from '@mui/material';
import clsx from 'clsx';

import { ReactComponent as CloseIcon } from 'assets/icons/CloseIcon.svg';
import { ReactComponent as Ellipsis } from 'assets/icons/EllipsisVertical.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/PlusIcon.svg';

import { useToggle } from 'hooks/useToggler';

import { Button } from 'components/_basic/Button/Button';
import { PopperComponent } from 'components/_basic/Popper/Popper';

import { IFormOptionsMenuProps, IRenderEachMenuOptionProps } from './types';

import styles from './FormOptionsMenu.module.scss';

const RenderEachMenuOption: FC<IRenderEachMenuOptionProps> = ({ title, icon, onClick, disabled }) => {
  return (
    <Box component="li" key={title} className={styles.listItem}>
      <Button disabled={disabled} fullWidth onClick={onClick} className={clsx(styles.button)} startIcon={icon}>
        <Typography variant="subtitle2" className={clsx(styles.titleStyles, { [styles.disabled]: disabled })}>
          {title}
        </Typography>
      </Button>
    </Box>
  );
};

export const FormOptionsMenu: FC<IFormOptionsMenuProps> = ({
  rowIndex,
  totalRowCount,
  disabled = false,
  onInsert,
  onRemove,
}) => {
  const { t } = useTranslation();

  const [isMenuShown, setIsMenuShown] = useToggle(false);

  const anchorEl = useRef<SVGSVGElement | null>(null);

  const handleToggle = () => setIsMenuShown(!isMenuShown);

  const handleCloseClick = () => setIsMenuShown(false);

  const onAddAbove = () => onInsert(rowIndex);
  const onAddBelow = () => onInsert(rowIndex + 1);

  const isRemoveOptionEnabled = totalRowCount > 1;

  return (
    <>
      <Ellipsis ref={anchorEl} onClick={handleToggle} className={styles.ellipsisStyle} />
      <PopperComponent
        open={isMenuShown}
        placement={'bottom-start'}
        className={styles.menuWrapper}
        anchorEl={anchorEl.current}
        onClose={handleCloseClick}
      >
        <Paper elevation={0} className={styles.menuOptionsContainer}>
          <Box component="ul" className={styles.listContainer} onClick={handleCloseClick}>
            <RenderEachMenuOption disabled={disabled} onClick={onAddAbove} title={t('addAbove')} icon={<PlusIcon />} />
            <RenderEachMenuOption disabled={disabled} onClick={onAddBelow} title={t('addBelow')} icon={<PlusIcon />} />
            <RenderEachMenuOption
              disabled={disabled || !isRemoveOptionEnabled}
              onClick={() => onRemove(rowIndex)}
              title={t('remove')}
              icon={<CloseIcon className={clsx(styles.removeIcon, { [styles.disabled]: !isRemoveOptionEnabled })} />}
            />
          </Box>
        </Paper>
      </PopperComponent>
    </>
  );
};
